import { useState } from 'react';
import { Loader2, Play, Plug2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function Verdict({ pass: ok }) {
  if (ok === null || ok === undefined) {
    return (
      <Badge variant="outline" className="gap-1 text-[10px]">
        N/A
      </Badge>
    );
  }
  if (ok) {
    return (
      <Badge className="bg-green-600 hover:bg-green-700 text-white gap-1 text-[10px]">
        <CheckCircle2 className="h-3 w-3" />
        PASS
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1 text-[10px]">
      <XCircle className="h-3 w-3" />
      FAIL
    </Badge>
  );
}

function EnvRow({ name, info }) {
  return (
    <tr className="border-b last:border-0">
      <td className="px-3 py-1.5 font-mono text-xs font-medium">{name}</td>
      <td className="px-3 py-1.5 text-xs">
        {info.present ? (
          <span className="text-green-600 dark:text-green-400">present</span>
        ) : (
          <span className="text-red-500 dark:text-red-400">missing</span>
        )}
      </td>
      <td className="px-3 py-1.5 text-xs text-muted-foreground font-mono">
        {info.last4 || (info.length ? `length=${info.length}` : info.value || info.hint || '')}
      </td>
    </tr>
  );
}

export function OBConnectDryrunPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.obConnectDryrun();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Plug2 className="h-5 w-5 text-blue-600" />
            OB Connect Dry-Run
          </CardTitle>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Run
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Pre-flight check for TrueLayer Open Banking. Verifies env vars, sandbox/prod consistency, callback URL, auth link shape, token refresh, and provider coverage. Read-only, no side effects.
        </p>
      </CardHeader>
      {result && (
        <CardContent className="space-y-4">
          {/* Overall banner */}
          <div className={`border rounded-lg p-3 ${result.overall_pass ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'}`}>
            <div className="flex items-center gap-2 text-sm font-medium">
              {result.overall_pass ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-green-700 dark:text-green-400">OB Connect ready</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <span className="text-red-700 dark:text-red-400">OB Connect will fail (see details below)</span>
                </>
              )}
            </div>
          </div>

          {/* Env vars table */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
              Environment Variables
            </h4>
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-3 py-1.5 font-medium">Variable</th>
                    <th className="text-left px-3 py-1.5 font-medium">Status</th>
                    <th className="text-left px-3 py-1.5 font-medium">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {result.env_vars && Object.entries(result.env_vars).map(([name, info]) => (
                    <EnvRow key={name} name={name} info={info} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Env/URL consistency */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
              Env/URL Consistency
            </h4>
            <div className="border rounded-lg p-3 space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <Verdict pass={result.consistency?.pass} />
                <span>{result.consistency?.reason}</span>
              </div>
            </div>
          </div>

          {/* Redirect URI */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
              Redirect URI
            </h4>
            <div className="border rounded-lg p-3 space-y-1 text-xs">
              <div>
                <span className="text-muted-foreground">OB_CALLBACK_URL: </span>
                <span className="font-mono">{result.redirect_uri?.ob_callback_url}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Effective redirect_uri: </span>
                <span className="font-mono">{result.redirect_uri?.effective_redirect_uri}</span>
              </div>
            </div>
          </div>

          {/* Auth link */}
          {result.auth_link && (
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                Auth Link (sample)
              </h4>
              <div className="border rounded-lg p-3 space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <Verdict pass={result.auth_link.pass} />
                  <span>{result.auth_link.pass ? 'Auth link builds successfully' : (result.auth_link.error || 'Failed')}</span>
                </div>
                {result.auth_link.url && (
                  <pre className="mt-1 p-2 bg-muted rounded text-[10px] overflow-x-auto whitespace-pre-wrap break-all">
                    {result.auth_link.url}
                  </pre>
                )}
              </div>
            </div>
          )}

          {/* Token refresh */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
              Token Refresh
            </h4>
            <div className="border rounded-lg p-3 space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <Verdict pass={result.token_refresh?.pass} />
                <span>{result.token_refresh?.detail}</span>
              </div>
              {result.token_refresh?.upstream_status && (
                <div className="text-red-600 dark:text-red-400">
                  <span className="font-medium">Upstream status: </span>
                  <span className="font-mono">{result.token_refresh.upstream_status}</span>
                </div>
              )}
              {result.token_refresh?.upstream_body && (
                <pre className="mt-1 p-2 bg-muted rounded text-[10px] overflow-x-auto whitespace-pre-wrap">
                  {result.token_refresh.upstream_body}
                </pre>
              )}
            </div>
          </div>

          {/* Providers coverage */}
          {result.providers && (
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                Provider Coverage
              </h4>
              <div className="border rounded-lg p-3 space-y-3 text-xs">
                <div className="flex items-center gap-2">
                  <Verdict pass={result.providers.pass} />
                  <span>
                    {result.providers.pass
                      ? `${result.providers.total_count} provider(s) returned`
                      : (result.providers.detail || 'Failed')}
                  </span>
                </div>

                {result.providers.upstream_status && (
                  <div className="text-red-600 dark:text-red-400">
                    <span className="font-medium">Upstream: </span>
                    <span className="font-mono">HTTP {result.providers.upstream_status}</span>
                  </div>
                )}
                {result.providers.upstream_body && (
                  <pre className="mt-1 p-2 bg-muted rounded text-[10px] overflow-x-auto whitespace-pre-wrap">
                    {result.providers.upstream_body}
                  </pre>
                )}

                {/* Target matches */}
                {result.providers.targets?.map((t, i) => (
                  <div key={i} className="space-y-1">
                    <div className="font-medium">
                      {t.target}: {t.count > 0 ? `${t.count} match(es)` : 'not found'}
                    </div>
                    {t.matches?.length > 0 && (
                      <div className="overflow-x-auto border rounded">
                        <table className="w-full text-[10px]">
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="text-left px-2 py-1 font-medium">provider_id</th>
                              <th className="text-left px-2 py-1 font-medium">display_name</th>
                              <th className="text-left px-2 py-1 font-medium">country</th>
                              <th className="text-left px-2 py-1 font-medium">availability</th>
                            </tr>
                          </thead>
                          <tbody>
                            {t.matches.map((m, j) => (
                              <tr key={j} className="border-b last:border-0">
                                <td className="px-2 py-1 font-mono">{m.provider_id}</td>
                                <td className="px-2 py-1">{m.display_name}</td>
                                <td className="px-2 py-1">{m.country}</td>
                                <td className="px-2 py-1">{m.availability || 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}

                {/* Registry vs real comparison */}
                {result.providers.registry_vs_real?.length > 0 && (
                  <div>
                    <div className="font-medium mb-1">Registry IDs (current scaffolded values):</div>
                    <div className="overflow-x-auto border rounded">
                      <table className="w-full text-[10px]">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-2 py-1 font-medium">Registry ID</th>
                            <th className="text-left px-2 py-1 font-medium">Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.providers.registry_vs_real.map((r, i) => (
                            <tr key={i} className="border-b last:border-0">
                              <td className="px-2 py-1 font-mono">{r.registry_id}</td>
                              <td className="px-2 py-1">{r.registry_name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {result.checked_at && (
            <p className="text-[10px] text-muted-foreground text-right">
              Checked at {new Date(result.checked_at).toLocaleString()}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
